const axios = require('axios')
const http = require('http')
const https = require('https')
const { isSyntheticUserAgent, SYNTHETIC_LOG_MARKER } = require('./syntheticUserAgent')

const isLive = true

// Per-request backend timeout (ms). RAISED 3500 -> 10000 (2026-07-16): the VRS
// public-orders PRICING endpoints (offers / named-prices / countries /
// subscriptions) genuinely run 2-4s server-side (Datastore/compute — confirmed by
// BACKEND_DIAG: calls are slow even with keepAlive socket REUSE, so it is backend
// compute, not connection churn). At 3.5s those calls ABORTED before completing, so
// the SSR cache could never populate -> perpetual cold misses. A 10s budget lets
// them succeed and fill the (durable, parent) cache. Env-tunable.
const HTTP_TIMEOUT_MS = parseInt(process.env.BACKEND_HTTP_TIMEOUT_MS || '10000', 10)

// ── REAL wall-clock request deadline (2026-07-20 /shop empty-grid incident) ─────
// axios `timeout` (above) is a SOCKET timeout: it only starts counting once the
// request has been assigned a socket. When all `maxSockets` are busy on slow/hung
// backend calls, a new request sits in the keepAlive agent's QUEUE — and axios has
// not armed its timer yet, so it can wait there for MINUTES. Observed live on the
// staging candidate: `/rest/public-orders/:cc/offers 434000ms TIMEOUT` — 7 minutes,
// far past the 45s worker render budget, which killed the render and cached an
// EMPTY page. HTTP_DEADLINE_MS is a hard ceiling on TOTAL request time (queue +
// connect + send + response), enforced with an AbortController armed the moment the
// request is created (see the request interceptor). No backend call can ever exceed
// it regardless of pool saturation; a stuck request aborts, frees its queue slot,
// and the SSR degrades fast to last-good / client-refetch instead of wedging the
// worker. Kept above HTTP_TIMEOUT_MS so a legit slow-but-answering call still hits
// the finer-grained socket timeout first in the common case. Env-tunable.
const HTTP_DEADLINE_MS = parseInt(process.env.BACKEND_HTTP_DEADLINE_MS || '12000', 10)

// ── keepAlive connection pooling (2026-07-16 backend-timeout incident) ─────────
// Reuse warm TCP+TLS connections to appspot instead of a fresh handshake per call
// (latency + CPU tax on the 1-CPU box). On by default; BACKEND_HTTP_KEEPALIVE=0
// falls back to per-call connections.
const KEEP_ALIVE = process.env.BACKEND_HTTP_KEEPALIVE !== '0'
const MAX_SOCKETS = parseInt(process.env.BACKEND_HTTP_MAX_SOCKETS || '64', 10)
const agentOpts = { keepAlive: KEEP_ALIVE, maxSockets: MAX_SOCKETS, maxFreeSockets: 16 }
const httpAgent = new http.Agent(agentOpts)
const httpsAgent = new https.Agent(agentOpts)

// ── Slow-endpoint tracking — ALWAYS ON (2026-07-16) ───────────────────────────
// A backend call slower than SLOW_MS is logged immediately (`[backend-slow]`), and
// EVERY endpoint's latency is aggregated into a periodic `[backend-endpoints]`
// summary (worst p95 first: n / avg / p95 / slow-count / timeouts). This is the
// data for the standing "WHICH endpoints are slow and why" investigation — it is
// on without needing BACKEND_DIAG so we always capture a wedge as it forms.
// BACKEND_DIAG=1 additionally logs verbose per-call lines (status + reused=).
const SLOW_MS = parseInt(process.env.BACKEND_SLOW_MS || '2000', 10)
const SUMMARY_MS = parseInt(process.env.BACKEND_DIAG_SUMMARY_MS || '60000', 10)
const BACKEND_DIAG = process.env.BACKEND_DIAG === '1'

const client = axios.create({
  timeout: HTTP_TIMEOUT_MS,
  httpAgent,
  httpsAgent,
  headers: {
    'x-api-key': process.env.VRS_API_KEY || 'test-rest-api-token',
  },
})

// Group calls by endpoint TYPE (collapse the per-country segment) so aggregation
// is per endpoint, not per region: /rest/public-orders/US/offers -> .../:cc/offers.
function endpointKey(url) {
  let p
  try {
    p = new URL(url).pathname
  } catch (_e) {
    p = String(url).split('?')[0]
  }
  return p.replace(/\/[A-Z]{2}(?=\/)/, '/:cc')
}

const endpoints = new Map() // key -> { n, sum, max, slow, timeouts, samples[] }
function pct(sorted, p) {
  if (!sorted.length) return 0
  return sorted[Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length))]
}
// ── Synthetic-caller marking (ops/monitoring) ─────────────────────────────────
// The stderr lines below ([backend-slow], [backend] ... failed:) feed log-based
// metrics, and a Cloud Run stderr entry carries NO httpRequest/userAgent field —
// only textPayload. So when a call was made on behalf of a synthetic caller (the
// deploy-gate sweep, an e2e suite — see utils/syntheticUserAgent.js) the line
// itself carries a trailing ` caller=synthetic` token, and the metric filters
// exclude on it. Customers' lines are byte-for-byte unchanged.
function callerMarker(cfg) {
  return cfg && cfg.syntheticCaller ? ` ${SYNTHETIC_LOG_MARKER}` : ''
}

// Apply opts.userAgent to an outgoing call: forwarded on the wire so the App
// Engine request log (`protoPayload.userAgent`) can exclude it, and remembered
// on the config so the stderr lines can be marked. Only a SYNTHETIC UA is ever
// forwarded — a customer's browser UA is never sent to the backend, which keeps
// their backend log entries exactly as they were (axios's default UA).
function applyCallerUserAgent(cfg, opts) {
  if (opts && isSyntheticUserAgent(opts.userAgent)) {
    cfg.headers = { ...(cfg.headers || {}), 'User-Agent': opts.userAgent }
    cfg.syntheticCaller = true
  }
  return cfg
}

function record(url, ms, kind, cfg) {
  const key = endpointKey(url)
  let e = endpoints.get(key)
  if (!e) {
    e = { n: 0, sum: 0, max: 0, slow: 0, timeouts: 0, samples: [] }
    endpoints.set(key, e)
  }
  e.n++
  e.sum += ms
  if (ms > e.max) e.max = ms
  if (ms >= SLOW_MS) e.slow++
  if (kind === 'timeout') e.timeouts++
  e.samples.push(ms)
  if (e.samples.length > 500) e.samples.shift()
  if (ms >= SLOW_MS || kind === 'timeout') {
    console.log(`[backend-slow] ${key} ${ms}ms${kind === 'timeout' ? ' TIMEOUT' : ''}${callerMarker(cfg)}`)
  }
}

client.interceptors.request.use((cfg) => {
  cfg.metadata = { start: Date.now() }
  // Arm the wall-clock deadline NOW (at request creation), so time spent QUEUED
  // waiting for a free socket counts against it — the gap axios `timeout` misses.
  // Don't clobber a caller-supplied signal. Timer is unref'd so it never keeps the
  // event loop alive, and cleared on settle (below) so it can't fire late.
  if (!cfg.signal) {
    const controller = new AbortController()
    cfg.signal = controller.signal
    // A caller may buy a LONGER budget for one genuinely slow endpoint (see
    // deadlineMs in post()); anything shorter than the default is ignored so a
    // caller can never tighten the shared ceiling by accident.
    const deadlineMs = Math.max(Number(cfg.deadlineMs) || 0, HTTP_DEADLINE_MS)
    const timer = setTimeout(() => controller.abort(), deadlineMs)
    if (timer.unref) timer.unref()
    cfg.metadata.deadlineTimer = timer
  }
  return cfg
})
function clearDeadline(cfg) {
  if (cfg && cfg.metadata && cfg.metadata.deadlineTimer) {
    clearTimeout(cfg.metadata.deadlineTimer)
    cfg.metadata.deadlineTimer = null
  }
}
client.interceptors.response.use(
  (res) => {
    clearDeadline(res.config)
    const ms = Date.now() - (res.config.metadata ? res.config.metadata.start : Date.now())
    record(res.config.url, ms, 'ok', res.config)
    if (BACKEND_DIAG) {
      const reused = res.request && res.request.reusedSocket
      console.log(`[backend-diag] ${(res.config.method || 'get').toUpperCase()} ${endpointKey(res.config.url)} -> ${res.status} in ${ms}ms reused=${!!reused}`)
    }
    return res
  },
  (err) => {
    const cfg = err.config || {}
    clearDeadline(cfg)
    const ms = Date.now() - (cfg.metadata ? cfg.metadata.start : Date.now())
    // The deadline AbortController rejects with ERR_CANCELED / CanceledError; the
    // socket timeout with ECONNABORTED. Both are "the backend did not answer in
    // time" → record as a timeout so it counts in the [backend-slow] TIMEOUT tally
    // and the callers' catch()es serve last-good / degrade, same as a socket timeout.
    const timedOut =
      err.code === 'ECONNABORTED' ||
      err.code === 'ERR_CANCELED' ||
      err.name === 'CanceledError'
    record(cfg.url, ms, timedOut ? 'timeout' : 'error', cfg)
    if (BACKEND_DIAG) {
      console.log(`[backend-diag] ${(cfg.method || 'get').toUpperCase()} ${endpointKey(cfg.url)} -> ${err.code || err.message} in ${ms}ms`)
    }
    return Promise.reject(err)
  }
)

// Periodic per-endpoint aggregate (worst p95 first) + live socket-pool sizes.
// Skipped under jest (NODE_ENV=test) so it never leaks an open handle in tests.
if (process.env.NODE_ENV !== 'test') {
  const timer = setInterval(() => {
    if (!endpoints.size) return
    const rows = [...endpoints.entries()]
      .map(([k, e]) => ({ k, n: e.n, avg: e.n ? Math.round(e.sum / e.n) : 0, p95: pct(e.samples.slice().sort((a, b) => a - b), 95), max: e.max, slow: e.slow, timeouts: e.timeouts }))
      .filter((r) => r.n > 0)
      .sort((a, b) => b.p95 - a.p95)
    if (!rows.length) return
    const activeH = Object.values(httpsAgent.sockets).reduce((s, a) => s + a.length, 0)
    const freeH = Object.values(httpsAgent.freeSockets || {}).reduce((s, a) => s + a.length, 0)
    console.log(
      `[backend-endpoints] ${SUMMARY_MS / 1000}s worst-first: ` +
        rows.slice(0, 10).map((r) => `${r.k} n=${r.n} avg=${r.avg} p95=${r.p95} max=${r.max} slow=${r.slow} to=${r.timeouts}`).join(' | ') +
        ` || https sockets active=${activeH} free=${freeH} keepAlive=${KEEP_ALIVE}`
    )
    for (const e of endpoints.values()) {
      e.n = 0
      e.sum = 0
      e.max = 0
      e.slow = 0
      e.timeouts = 0
      e.samples.length = 0
    }
  }, SUMMARY_MS)
  timer.unref && timer.unref()
}

// Exported for tests (deadline/timeout behavior) — the shared axios instance.
export { client }

export const getPublicOrdersApiUrl = () => {
  if (isLive) {
    return 'https://virtualracingschool.appspot.com/rest/public-orders'
  } else {
    return 'http://localhost:8888/rest/public-orders'
  }
}

// ── Retry policy for IDEMPOTENT GETs (2026-08-28) ─────────────────────────────
// `get()` is a read-only proxy of a GET, so re-issuing it is side-effect free.
// Measured on prod: /rest/public-orders/countries answers in 29-150ms in the
// steady state but occasionally stalls past the 10s socket timeout — and the very
// next call to the SAME endpoint succeeded in 3653ms, then 2038ms (candidate,
// 2026-08-28T03:59:54Z). A single retry recovers that class outright.
//
// RETRY ONLY WHAT IS SAFE TO RETRY:
//   - a TIMEOUT (socket ECONNABORTED / deadline ERR_CANCELED / CanceledError),
//   - a transport error with NO response (ECONNRESET/ENOTFOUND/EAI_AGAIN/…), or
//   - a 5xx / 429 response — the upstream told us it could not answer THIS time.
// A 4xx (other than 429) is a DETERMINISTIC answer about the request itself:
// retrying it wastes the budget and can never change the outcome, so it is
// returned/thrown immediately. Opt-in per call (`retries`), so no existing caller
// silently doubles its latency budget.
const RETRY_BASE_DELAY_MS = parseInt(process.env.BACKEND_RETRY_BASE_MS || '250', 10)

export function isRetryableBackendError(error) {
  if (!error) return false
  const status = error.response && error.response.status
  if (status) return status >= 500 || status === 429
  // No response at all: a timeout, an abort, or a transport-level failure.
  return true
}

function retryDelayMs(attempt) {
  // Exponential with jitter, so a burst of concurrent callers does not re-stampede
  // the stalled upstream in lockstep.
  return RETRY_BASE_DELAY_MS * 2 ** (attempt - 1) + Math.floor(Math.random() * RETRY_BASE_DELAY_MS)
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// opts.timeoutMs raises BOTH the socket timeout and the wall-clock deadline for a
// single call — same contract as post(). The PRICED surfaces need it: offers /
// named-prices / shipping-info each run 2.7-5.0s server-side, and a COLD instance
// fetching several of them at once blows the shared 10s budget outright. When that
// happens the surface degrades to no price at all, which is how a fully-rendered
// page ends up with no price on it (`[regionData] namedPrices fetch failed: timeout
// of 10000ms exceeded`, candidate, 2026-08-23) — and why the deploy gate's
// region-currency check kept failing on an otherwise healthy build.
//
// opts.retries (default 0) adds N extra attempts for the retryable classes above.
//
// opts.userAgent — the inbound caller's UA, forwarded to the backend ONLY when it
// is synthetic (utils/syntheticUserAgent.js). API routes obtain it with
// `backendCallOpts(request, opts)` from utils/appRouteRequest.js.
export async function get(url, opts = {}) {
  const cfg = applyCallerUserAgent({}, opts)
  if (opts.timeoutMs) {
    cfg.timeout = Math.max(opts.timeoutMs, HTTP_TIMEOUT_MS)
    cfg.deadlineMs = Math.max(opts.timeoutMs + 2000, HTTP_DEADLINE_MS)
  }
  const extraAttempts = Math.max(0, Number(opts.retries) || 0)

  for (let attempt = 1; ; attempt += 1) {
    try {
      return await client.get(url, cfg)
    } catch (error) {
      const canRetry = attempt <= extraAttempts && isRetryableBackendError(error)
      if (!canRetry) {
        // Concise failure log — regionData.js already reports the per-surface
        // `[regionData] X fetch failed: <message>`; we don't re-dump the AxiosError.
        console.error(
          `[backend] GET ${endpointKey(url)} failed: ${error.code || error.message}` +
            (attempt > 1 ? ` (after ${attempt} attempts)` : '') +
            callerMarker(cfg)
        )
        throw error
      }
      const delay = retryDelayMs(attempt)
      console.warn(
        `[backend] GET ${endpointKey(url)} attempt ${attempt}/${extraAttempts + 1} failed ` +
          `(${error.code || error.message}); retrying in ${delay}ms${callerMarker(cfg)}`
      )
      await sleep(delay)
    }
  }
}

// opts.timeoutMs raises BOTH the socket timeout and the wall-clock deadline for a
// single call. Needed because the invoice preview prices a whole cart server-side
// and the largest bundles measured >10s (2026-08-23), so they aborted every time
// on the shared 10s/12s budget.
//
// opts.headers merges extra request headers on top of the JSON content-type. Only
// the order money path uses it today, to carry `Idempotency-Key` (see
// app/api/payments/makeOrderAndStartPayment/route.js): the whole point of that
// header is that the SAME value reaches the backend on a retry, so it has to
// travel per-call rather than on the shared client.
//
// opts.userAgent — same contract as get(). The `[backend] POST ... failed:` line
// below is what the checkout_proxy_call_failures metric reads; a synthetic
// caller's line ends in ` caller=synthetic` so the metric can exclude it.
export function post(url, body = {}, opts = {}) {
  const cfg = applyCallerUserAgent({ headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) } }, opts)
  if (opts.timeoutMs) {
    cfg.timeout = Math.max(opts.timeoutMs, HTTP_TIMEOUT_MS)
    cfg.deadlineMs = Math.max(opts.timeoutMs + 2000, HTTP_DEADLINE_MS)
  }
  return client.post(url, body, cfg).catch((error) => {
    console.error(`[backend] POST ${endpointKey(url)} failed: ${error.code || error.message}${callerMarker(cfg)}`)
    throw error
  })
}
